import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthHttpClient } from '../auth-http-service/auth-http.service';
import { LoggingService } from '../logging-service/logging.service';
import { AppConfigService } from '../app-config-service/app-config.service';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { Observable } from 'rxjs';
import { ShellUpdateService } from '../shell-update-service/shell-update.service';
import { MatSnackBar } from '@angular/material';
import { PatientDataService } from '../patient-data/patient-data.service';
import { logging } from 'protractor';
import { PortalLoginViewComponent } from '../../views/portal-login-view/portal-login-view.component';
import { Credentials } from '../../domain/credentials';
import { IfObservable } from 'rxjs/observable/IfObservable';
import { TrustDataService } from '../trust-data-service/trust-data.service';
import { map } from 'rxjs/operators';
import { strictEqual } from 'assert';
import { HomeCardModel } from '../../models/home-card-model';


@Injectable()
export class UserSessionService {

  private loginSubscription: Subscription;
  private trustCardSubscription: Subscription;

  private patient: any;
  public homeCards: Array<HomeCardModel>;
  loggedIn: boolean;

  // Services are injected here
  constructor(private shellUpdateService: ShellUpdateService,
    private appConfigService: AppConfigService, private router: Router, private route: ActivatedRoute,
    private authHttp: AuthHttpClient, public loggingService: LoggingService, private snackBar: MatSnackBar,
    private patientDataService: PatientDataService, private trustDataService: TrustDataService) { }
  public HasAdmin: Subject<boolean> = new Subject<boolean>();

  /* Logs a user out **/
  logout() {
    this.loggingService.log('Clearing identifiers', this.appConfigService.LogToConsole);
    this.clearIdentifiers();
    this.loggingService.log('Removing user session token', this.appConfigService.LogToConsole);
    sessionStorage.removeItem('BearerToken');
    this.loggingService.log('Redirecting to welcome', this.appConfigService.LogToConsole);
    this.router.navigate(['app/welcome']);
  }

  private clearIdentifiers() {
    this.appConfigService.PatientId = null;
    this.appConfigService.PregnancyId = null;
    this.appConfigService.PatientInitials = null;
    this.appConfigService.IsTrust = false;
    this.authHttp.Credentials = null;
  }

  checkRoute(url: string) {

    // If user, check for admin areas
    if (this.appConfigService.IsTrust === false) {
      let isAdminArea = false;
      // Where is the user trying to go? They are not allowed in admin views
      // Check to see if the user is operating in an adminsitrative area
      for (let index = 0; index < this.appConfigService.RestrictedAreas.length; index++) {
        const area = this.appConfigService.RestrictedAreas[index];
        // If the user isn't an admin. don't give them access
        this.loggingService.log(`Checking if restricted area: ${area}:${this.router.url}`, this.appConfigService.LogToConsole);
        if (this.router.url !== '/' && area.startsWith(this.router.url)) {
          this.loggingService.log(`${area} is restricted`, this.appConfigService.LogToConsole);
          isAdminArea = true;
        }
      }
      if (isAdminArea && !this.appConfigService.IsTrust) {
        this.router.navigate(['app', 'home']);
        return;
      }
    }

    // If admin, check for user areas
    if (this.appConfigService.IsTrust === true) {
      let isUserArea = false;
      // Where is the user trying to go? They are not allowed in admin views
      // Check to see if the user is operating in an adminsitrative area
      for (let index = 0; index < this.appConfigService.UserAreas.length; index++) {
        const area = this.appConfigService.UserAreas[index];
        // If the user isn't an admin. don't give them access
        this.loggingService.log(`Checking if restricted area: ${area}:${this.router.url}`, this.appConfigService.LogToConsole);
        if (this.router.url !== '/' && area.startsWith(this.router.url)) {
          this.loggingService.log(`${area} is out of context`, this.appConfigService.LogToConsole);
          isUserArea = true;
        }
      }
      if (isUserArea && this.appConfigService.IsTrust) {
        this.router.navigate(['app', 'admin', 'search']);
        return;
      }
    }
  }

  /** Logs a user in */
  login(credentials: any): Observable<boolean> {

    this.authHttp.Credentials = credentials;
    this.appConfigService.IsTrust = false;

    const authHttp = this.authHttp;
    const appConfigService = this.appConfigService;
    const loggingService = this.loggingService;
    const patientDataService = this.patientDataService;
    const router = this.router;
    const route = this.route;
    const snackBar = this.snackBar;
    let patient = this.patient;


    /** Create an observable to which this function returns. Notify on changes, and return it */
    const observable = Observable.create(function subscribe(observer) {
      // No need to do lots of verification if we have a patient id
      // as the user has already logged in and NOT refreshed the browser
      // using the refresh browser button.
      if (appConfigService.PatientId !== undefined && appConfigService.PatientId !== null) {
        loggingService.log('Using cached patient id', appConfigService.LogToConsole);
        observer.next(true);
        observer.complete();
        return;
      }

      // We don't have a patient id. User could have hit the refresh button on the browser.
      // In which case we need to check for either credentials or a token in sessionStorage
      // if there are no credentials and no token, this is a new session.
      if (authHttp.Credentials === undefined || authHttp.Credentials === null) {
        if (authHttp.token() === undefined || authHttp.token() === null || authHttp.token() === '') {
          loggingService.log('New session', appConfigService.LogToConsole);
          loggingService.log('Not authenticated', appConfigService.LogToConsole);
          observer.next(false);
          observer.complete();
          router.navigate(['app', 'login']);
          return;
        }
      }

      // We either have credentials (user name and password) or a token
      // Post what we have to the authentication url, and wait for the response.
      // Will convert all this to a fork join when I have more time.
      authHttp.post(appConfigService.AuthenticationLogInUrl, null).subscribe((response) => {

        // User Login
        loggingService.log('User logged in', appConfigService.LogToConsole);
        authHttp.saveToken(response);
        this.loggedIn = true;
        console.log(this.loggedIn);
        loggingService.log('Saved token', appConfigService.LogToConsole);

        // Now the user is logged in, lets check what they have access to
        loggingService.log(`Checking user role`, appConfigService.LogToConsole);
        const userProperties = response.json();

        // Set the flag around the system so we know what they have access to
        appConfigService.IsTrust = userProperties.IsTrust;
        appConfigService.UserName = userProperties.UserName;

        // The next step is to get the patient id
        if (appConfigService.IsTrust === false) {
          loggingService.log(`Attempting to get patient id for: ` +
            appConfigService.UserName, appConfigService.LogToConsole);

          patientDataService.getPatientId(appConfigService.UserName).subscribe(patientIdentity => {

            const responseJson = patientIdentity.json();
            appConfigService.PatientId = responseJson.PatientId;
            loggingService.log(`patient id:` + responseJson.PatientId, appConfigService.LogToConsole);
            loggingService.log(`Getting patient`, appConfigService.LogToConsole);

            patientDataService.getPatient(appConfigService.PatientId).subscribe(patientResponse => {
              patient = patientResponse.json();
              appConfigService.Forename = patient.Forenames;
              loggingService.log(`Got patient ` + patient.Forenames, appConfigService.LogToConsole);
              appConfigService.PatientInitials = patient.Forenames.substring(0, 1) + patient.Surname.substring(0, 1);

              if (patient.CurrentPregnancy) {
                appConfigService.PregnancyId = patient.CurrentPregnancy.PregnancyId;
              }

              // Notify the subscription
              this.loggedIn = true;
              observer.next(true);
              observer.complete();
              loggingService.log('Logged in', appConfigService.LogToConsole);
            });
          }, (error => {
            const errorResponse = error as Response;
            loggingService.log(`Error while getting patient: ` + errorResponse.statusText, appConfigService.LogToConsole);

            // Notify the subscription
            this.loggedIn = false;
            observer.next(false);
            observer.complete();
          }));
        } else {

          // Trust login
          appConfigService.Forename = appConfigService.UserName;
          appConfigService.PatientInitials = appConfigService.Forename.substring(0, 1);
          this.loggedin = true;
          observer.next(true);
          observer.complete();
        }
      }, (error) => {
        const errorResponse = error as HttpErrorResponse;
        loggingService.log('Error logging in user', appConfigService.LogToConsole);
        authHttp.deleteToken();
        // Just in case you are wondering, the user may have a token, but It could have expired.
        // This code is also executed by anything where a routeGuard is in place.
        // So if we get a 401, always redirect back to login
        switch (errorResponse.status) {
          case 401:
            if (credentials) {
              snackBar.open('Sorry, unable to log in. Invalid email address or password.', '', { duration: 5000 });
            }
            break;
          case 500:
            snackBar.open('Server error. Please try again later.', '', { duration: 5000 });
            break;
          default:
            snackBar.open('Server unavailable. Please try again later.', '', { duration: 5000 });
            break;
        }
        // Notify the subscription
        this.loggedIn = false;
        observer.next(false);
        observer.complete();
        router.navigate(['app/login']);
      });
    });



    return observable;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/user-session-service/user-session.service.ts